import { useEffect, useRef } from "react";

// ─── Dot-matrix world map with dithered ship traffic ──────────────────────
// Background layer for the login page's top/bottom whitespace. One canvas:
// a static bitmap landmass drawn once to an offscreen layer, then blitted
// each frame with animated ships on top, so the per-frame cost is a handful
// of ship cells rather than ~22k land dots.

const MAP_COLS = 76;
const MAP_ROWS = 33;

// Equirectangular land mask, hand-plotted as row → [startCol, endCol] spans.
// ~4.7° of longitude per column, ~4.3° of latitude per row; row 0 is ~83°N.
// Spans rather than a 76-char-wide string block per row: a string blob is
// impossible to review or amend in a diff, whereas a span is obvious — widen
// Africa by two columns and you can see exactly that in the patch.
// Plotted from real coastlines rather than by eye. To check or amend a span:
// column = (lon + 180) / 4.7368, row = (83 - lat) / 4.3.
const LAND_SPANS: Record<number, ReadonlyArray<readonly [number, number]>> = {
  0: [[19, 24], [26, 33]],
  1: [[12, 25], [26, 33], [40, 42], [49, 52], [57, 61]],
  2: [[10, 24], [26, 33], [41, 44], [46, 72]],
  3: [[3, 8], [9, 25], [26, 32], [40, 44], [45, 75]],
  4: [[2, 8], [9, 26], [27, 29], [32, 34], [38, 44], [45, 75]],
  5: [[3, 8], [9, 26], [28, 29], [38, 44], [45, 75]],
  6: [[2, 10], [11, 26], [35, 37], [39, 44], [45, 75]],
  7: [[10, 26], [36, 44], [45, 68]],
  8: [[11, 24], [37, 44], [45, 67]],
  9: [[11, 23], [36, 43], [44, 66]],
  10: [[12, 22], [36, 43], [44, 63], [64, 65], [66, 67]],
  11: [[12, 21], [35, 63], [65, 67]],
  12: [[13, 21], [35, 63]],
  13: [[14, 17], [20, 21], [34, 45], [46, 50], [52, 63]],
  14: [[15, 17], [20, 22], [34, 46], [47, 49], [53, 55], [57, 61]],
  15: [[16, 19], [21, 24], [34, 47], [53, 55], [57, 61], [63, 64]],
  16: [[18, 20], [22, 25], [34, 48], [53, 54], [57, 61], [63, 64]],
  17: [[21, 25], [35, 47], [58, 62], [63, 64]],
  18: [[21, 27], [36, 46], [59, 62]],
  19: [[22, 28], [39, 46], [58, 62], [63, 64]],
  20: [[22, 30], [40, 46], [59, 63], [65, 69]],
  21: [[21, 30], [40, 46], [60, 65], [66, 69]],
  22: [[22, 30], [40, 46], [64, 64], [65, 68]],
  23: [[23, 29], [40, 46], [47, 48], [62, 69]],
  24: [[23, 29], [40, 45], [47, 48], [61, 70]],
  25: [[23, 27], [41, 45], [47, 48], [61, 70]],
  26: [[22, 27], [41, 44], [62, 70]],
  27: [[22, 26], [41, 43], [62, 69]],
  28: [[22, 24], [68, 69], [74, 75]],
  29: [[22, 24], [68, 68], [73, 74]],
  30: [[22, 23], [73, 73]],
  31: [[22, 23]],
  32: [[23, 23]],
};

// 4x4 ordered-dither threshold matrix. The wake behind each ship is a smooth
// intensity ramp; thresholding it per-cell against this is what breaks the
// ramp into a stipple that dissolves, instead of a soft alpha fade that would
// read as a blur at this dot pitch.
const BAYER_4 = [0, 8, 2, 10, 12, 4, 14, 6, 3, 11, 1, 9, 15, 7, 13, 5];

// Shipping lanes in base-grid coordinates. These are placed for the two bands
// the page's mask actually reveals — roughly rows 0-8 up top and rows 24-32
// down below. A lane through the tropics would be geographically sensible and
// completely invisible, sitting behind the content cluster the whole time.
type Lane = ReadonlyArray<readonly [number, number]>;
const LANES: ReadonlyArray<{ path: Lane; speed: number; phase: number }> = [
  // North Atlantic — US east coast up to the British Isles.
  { path: [[23, 9], [27, 8], [32, 7], [36, 6.6]], speed: 0.05, phase: 0 },
  { path: [[23, 9], [27, 8], [32, 7], [36, 6.6]], speed: 0.05, phase: 0.55 },
  // Northern Sea Route — Norwegian Sea across the Russian arctic.
  { path: [[42, 5], [49, 3.4], [57, 3.2], [65, 4.6], [69, 6.4]], speed: 0.036, phase: 0.2 },
  // Iceland / Denmark Strait feeder.
  { path: [[29, 5.6], [33, 4.6], [37, 4.9], [40, 5.4]], speed: 0.045, phase: 0.72 },
  // North Sea / Baltic short-sea traffic.
  { path: [[36, 6.8], [39, 6.3], [42, 6.1], [44, 6.4]], speed: 0.055, phase: 0.4 },
  // North-west Pacific — Japan up past the Kurils toward the Aleutians. Only
  // the western leg: the lanes interpolate in plain grid space with no wrap,
  // so a single path through the dateline would draw a ship streaking right
  // back across Eurasia instead of reappearing on the far edge.
  { path: [[65, 9], [69, 7], [73, 5.6], [75, 5]], speed: 0.038, phase: 0.6 },
  // Cape of Good Hope — South Atlantic round into the Indian Ocean.
  { path: [[30, 26], [36, 28], [42, 28.6], [48, 27.4], [53, 26.3]], speed: 0.034, phase: 0.35 },
  { path: [[30, 26], [36, 28], [42, 28.6], [48, 27.4], [53, 26.3]], speed: 0.034, phase: 0.85 },
  // South Atlantic — Brazil across to west Africa.
  { path: [[28, 24.5], [33, 25.6], [38, 26.2]], speed: 0.045, phase: 0.15 },
  // Southern Ocean — Australia across to New Zealand.
  { path: [[61, 28.2], [66, 29.4], [71, 29.6], [75, 29]], speed: 0.042, phase: 0.45 },
  // Cape Horn.
  { path: [[23, 30], [25.5, 31.4], [29, 31], [33, 29.6]], speed: 0.04, phase: 0.1 },
  // Indian Ocean — Australia west toward southern Africa.
  { path: [[59, 25.5], [54, 26.4], [49, 27]], speed: 0.04, phase: 0.65 },
];

// Map height as a fraction of the container height — deliberately near-full,
// so the map runs the whole top-to-bottom length of the page rather than
// floating in a short strip. The consequence is horizontal overflow (the map
// is ~2.3:1), which is handled by the right-anchored crop in layout().
const MAP_HEIGHT_RATIO = 0.92;

const WAKE_STEPS = 13;
const WAKE_GAP = 0.011;
const TARGET_FPS = 20;

function buildLandMask(): Uint8Array {
  const mask = new Uint8Array(MAP_COLS * MAP_ROWS);
  for (const [row, spans] of Object.entries(LAND_SPANS)) {
    const r = Number(row);
    for (const [from, to] of spans) {
      for (let c = from; c <= to && c < MAP_COLS; c += 1) mask[r * MAP_COLS + c] = 1;
    }
  }
  return mask;
}

// Cumulative arc length so a ship moves at a constant rate along the polyline
// instead of sprinting through short segments and crawling through long ones.
function measure(path: Lane) {
  const acc = [0];
  for (let i = 1; i < path.length; i += 1) {
    const [px, py] = path[i - 1];
    const [x, y] = path[i];
    acc.push(acc[i - 1] + Math.hypot(x - px, y - py));
  }
  return { acc, total: acc[acc.length - 1] };
}

function pointAt(path: Lane, measured: { acc: number[]; total: number }, t: number) {
  // Ping-pong along the lane rather than wrapping. These are open polylines,
  // so a plain `t % 1` would snap the ship from the far end straight back to
  // the start every cycle — a visible teleport, with its wake stretched
  // across the map behind it. Out-and-back also just reads correctly: this is
  // round-trip freight, not a loop.
  const cycle = (((t % 1) + 1) % 1) * 2;
  const u = cycle <= 1 ? cycle : 2 - cycle;
  const target = u * measured.total;
  let i = 1;
  while (i < measured.acc.length - 1 && measured.acc[i] < target) i += 1;
  const span = measured.acc[i] - measured.acc[i - 1] || 1;
  const k = (target - measured.acc[i - 1]) / span;
  const [ax, ay] = path[i - 1];
  const [bx, by] = path[i];
  return { x: ax + (bx - ax) * k, y: ay + (by - ay) * k };
}

export function WorldMapField({ className }: { className?: string }) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const mask = buildLandMask();
    const measured = LANES.map((lane) => measure(lane.path));
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const css = getComputedStyle(document.documentElement);
    const token = (name: string) => css.getPropertyValue(name).trim();
    const landColor = `hsl(${token("--muted-foreground")} / 0.38)`;
    const shipColor = `hsl(${token("--primary")})`;
    const wakeColor = `hsl(${token("--primary")} / 0.55)`;

    // Geometry, recomputed on resize.
    //
    // A world map is ~2.3:1 and this panel is close to square, so the two
    // cannot both be satisfied: sizing the map to the panel width leaves it
    // far too short to reach the top and bottom bands, and it disappears
    // entirely behind the mask. So the map is sized off the panel HEIGHT —
    // tall enough that its northern and southern latitudes land squarely in
    // the two bands — and allowed to overflow horizontally.
    //
    // The overflow is anchored to the RIGHT edge, which crops the Pacific and
    // the US west coast off the left. That is the cheapest thing to lose: it
    // is nearly all open ocean, and it keeps the Atlantic, Europe, Africa,
    // the Gulf, India and south-east Asia — where the trade lanes are — in
    // frame.
    let cell = 0;
    let sub = 1;
    let cols = 0;
    let rows = 0;
    let offsetX = 0;
    let offsetY = 0;
    let dot = 0;
    let land: HTMLCanvasElement | null = null;

    function layout() {
      const rect = canvas.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      if (rect.width < 2 || rect.height < 2) return false;

      canvas.width = Math.round(rect.width * dpr);
      canvas.height = Math.round(rect.height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      const mapH = rect.height * MAP_HEIGHT_RATIO;
      const mapW = (mapH * MAP_COLS) / MAP_ROWS;

      // Supersample the 76x33 source so the dot pitch lands near 7px at any
      // size — nearest-neighbour, so the silhouette stays identical and only
      // the stipple gets finer.
      sub = Math.max(1, Math.round(mapW / MAP_COLS / 7));
      cols = MAP_COLS * sub;
      rows = MAP_ROWS * sub;
      cell = mapW / cols;
      dot = Math.max(1, cell * 0.5);
      offsetX = rect.width - mapW;
      offsetY = (rect.height - mapH) / 2;

      land = document.createElement("canvas");
      land.width = canvas.width;
      land.height = canvas.height;
      const lctx = land.getContext("2d");
      if (!lctx) return false;
      lctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      lctx.fillStyle = landColor;
      for (let r = 0; r < rows; r += 1) {
        const sr = Math.floor(r / sub);
        for (let c = 0; c < cols; c += 1) {
          if (!mask[sr * MAP_COLS + Math.floor(c / sub)]) continue;
          lctx.fillRect(offsetX + c * cell, offsetY + r * cell, dot, dot);
        }
      }
      return true;
    }

    function drawShips(clock: number) {
      for (let i = 0; i < LANES.length; i += 1) {
        const lane = LANES[i];
        const t = lane.phase + clock * lane.speed;

        // Dithered wake first, so the solid hull sits on top of it.
        ctx.fillStyle = wakeColor;
        for (let s = 1; s <= WAKE_STEPS; s += 1) {
          const p = pointAt(lane.path, measured[i], t - s * WAKE_GAP);
          const intensity = 1 - s / WAKE_STEPS;
          const baseC = Math.round(p.x * sub);
          const baseR = Math.round(p.y * sub);
          for (let dy = 0; dy < 2; dy += 1) {
            for (let dx = 0; dx < 2; dx += 1) {
              const c = baseC + dx;
              const r = baseR + dy;
              const threshold = (BAYER_4[(c & 3) + (r & 3) * 4] + 0.5) / 16;
              if (intensity <= threshold) continue;
              ctx.fillRect(offsetX + c * cell, offsetY + r * cell, dot, dot);
            }
          }
        }

        const head = pointAt(lane.path, measured[i], t);
        const hc = Math.round(head.x * sub);
        const hr = Math.round(head.y * sub);
        ctx.fillStyle = shipColor;
        for (let dy = 0; dy < 2; dy += 1) {
          for (let dx = 0; dx < 2; dx += 1) {
            ctx.fillRect(offsetX + (hc + dx) * cell, offsetY + (hr + dy) * cell, dot, dot);
          }
        }
      }
    }

    function render(clock: number) {
      // `land` is null until the first successful layout, and a zero-sized
      // source canvas makes drawImage throw InvalidStateError outright.
      if (!land || land.width < 1 || land.height < 1) return;
      const rect = canvas.getBoundingClientRect();
      if (rect.width < 2 || rect.height < 2) return;
      ctx.clearRect(0, 0, rect.width, rect.height);
      ctx.drawImage(land, 0, 0, rect.width, rect.height);
      drawShips(clock);
    }

    let raf = 0;
    let start = 0;
    let last = 0;
    const frameMs = 1000 / TARGET_FPS;

    function loop(now: number) {
      raf = requestAnimationFrame(loop);
      if (!start) start = now;
      if (now - last < frameMs) return;
      last = now;
      render((now - start) / 1000);
    }

    // Observe FIRST, and never bail out of the effect on a failed measurement.
    // This layer is `hidden lg:block`, so on a narrow viewport — and on the
    // first tick before layout settles — the canvas measures 0x0. Treating
    // that as fatal (the obvious `if (!layout()) return`) means the map stays
    // permanently blank once the window is later widened past lg, because
    // nothing is left listening for the resize.
    const observer = new ResizeObserver(() => {
      if (!layout()) return;
      render(reduced || !start ? 0 : (performance.now() - start) / 1000);
    });
    observer.observe(canvas);

    layout();

    if (reduced) {
      // Still a map, still ships — just held at their opening positions.
      render(0);
    } else {
      raf = requestAnimationFrame(loop);
    }

    return () => {
      cancelAnimationFrame(raf);
      observer.disconnect();
    };
  }, []);

  return <canvas ref={canvasRef} className={className} aria-hidden="true" />;
}
